import { create } from 'zustand';
import { 
  Admission, 
  Discharge, 
  Transfer, 
  Ward, 
  Room, 
  Bed,
  ADTNotification,
  ADTStats,
  AdmissionStatus,
  DischargeStatus,
  TransferStatus
} from '../types/adt';

interface ADTStore {
  // State
  admissions: Admission[];
  discharges: Discharge[];
  transfers: Transfer[];
  wards: Ward[];
  notifications: ADTNotification[];
  activeAdmission: Admission | null;
  activeDischarge: Discharge | null;
  activeTransfer: Transfer | null;
  
  // Actions - Admissions
  setAdmissions: (admissions: Admission[]) => void;
  addAdmission: (admission: Admission) => void;
  updateAdmission: (admission: Admission) => void;
  setActiveAdmission: (admission: Admission | null) => void;
  updateAdmissionStatus: (admissionId: string, status: AdmissionStatus) => void;
  
  // Actions - Discharges
  setDischarges: (discharges: Discharge[]) => void;
  addDischarge: (discharge: Discharge) => void;
  updateDischarge: (discharge: Discharge) => void;
  setActiveDischarge: (discharge: Discharge | null) => void;
  updateDischargeStatus: (dischargeId: string, status: DischargeStatus) => void;
  
  // Actions - Transfers
  setTransfers: (transfers: Transfer[]) => void;
  addTransfer: (transfer: Transfer) => void;
  updateTransfer: (transfer: Transfer) => void;
  setActiveTransfer: (transfer: Transfer | null) => void;
  updateTransferStatus: (transferId: string, status: TransferStatus) => void;
  
  // Actions - Wards & Beds
  setWards: (wards: Ward[]) => void;
  updateBedStatus: (bedId: string, status: Bed['status'], patientId?: string) => void;
  assignBed: (patientId: string, bedId: string) => void;
  releaseBed: (bedId: string) => void;
  
  // Actions - Notifications
  addNotification: (notification: ADTNotification) => void;
  acknowledgeNotification: (notificationId: string, acknowledgedBy: string) => void;
  
  // Computed
  getAdmissionsByPatient: (patientId: string) => Admission[];
  getActiveAdmissions: () => Admission[];
  getAvailableBeds: (wardId?: string) => Bed[];
  getBedById: (bedId: string) => Bed | null;
  getRoomById: (roomId: string) => Room | null;
  getWardById: (wardId: string) => Ward | null;
  getADTStats: () => ADTStats;
  getCurrentPatientAdmission: (patientId: string) => Admission | null;
}

export const useADTStore = create<ADTStore>((set, get) => ({
  // Initial State
  admissions: [],
  discharges: [],
  transfers: [],
  wards: [],
  notifications: [],
  activeAdmission: null,
  activeDischarge: null,
  activeTransfer: null,
  
  // Admissions Actions
  setAdmissions: (admissions) => set({ admissions }),
  
  addAdmission: (admission) => set((state) => ({
    admissions: [...state.admissions, admission]
  })),
  
  updateAdmission: (admission) => set((state) => ({
    admissions: state.admissions.map(a => a.id === admission.id ? admission : a)
  })),
  
  setActiveAdmission: (admission) => set({ activeAdmission: admission }),
  
  updateAdmissionStatus: (admissionId, status) => set((state) => ({
    admissions: state.admissions.map(a => 
      a.id === admissionId ? { ...a, status } : a
    )
  })),
  
  // Discharges Actions
  setDischarges: (discharges) => set({ discharges }),
  
  addDischarge: (discharge) => set((state) => ({
    discharges: [...state.discharges, discharge]
  })),
  
  updateDischarge: (discharge) => set((state) => ({
    discharges: state.discharges.map(d => d.id === discharge.id ? discharge : d)
  })),
  
  setActiveDischarge: (discharge) => set({ activeDischarge: discharge }),
  
  updateDischargeStatus: (dischargeId, status) => set((state) => ({
    discharges: state.discharges.map(d => 
      d.id === dischargeId ? { ...d, status } : d
    )
  })),
  
  // Transfers Actions
  setTransfers: (transfers) => set({ transfers }),
  
  addTransfer: (transfer) => set((state) => ({
    transfers: [...state.transfers, transfer]
  })),
  
  updateTransfer: (transfer) => set((state) => ({
    transfers: state.transfers.map(t => t.id === transfer.id ? transfer : t)
  })),
  
  setActiveTransfer: (transfer) => set({ activeTransfer: transfer }),
  
  updateTransferStatus: (transferId, status) => set((state) => ({
    transfers: state.transfers.map(t => 
      t.id === transferId ? { ...t, status } : t
    )
  })),
  
  // Wards & Beds Actions
  setWards: (wards) => set({ wards }),
  
  updateBedStatus: (bedId, status, patientId) => set((state) => ({
    wards: state.wards.map(ward => ({
      ...ward,
      rooms: ward.rooms.map(room => ({
        ...room,
        beds: room.beds.map(bed => 
          bed.id === bedId 
            ? { ...bed, status, isOccupied: status === 'occupied', patientId }
            : bed
        )
      }))
    }))
  })),
  
  assignBed: (patientId, bedId) => {
    const { updateBedStatus } = get();
    updateBedStatus(bedId, 'occupied', patientId);
  },
  
  releaseBed: (bedId) => {
    const { updateBedStatus } = get();
    updateBedStatus(bedId, 'cleaning', undefined);
  },
  
  // Notifications Actions
  addNotification: (notification) => set((state) => ({
    notifications: [...state.notifications, notification]
  })),
  
  acknowledgeNotification: (notificationId, acknowledgedBy) => set((state) => ({
    notifications: state.notifications.map(n => 
      n.id === notificationId 
        ? { ...n, acknowledgedAt: new Date().toISOString(), acknowledgedBy }
        : n
    )
  })),
  
  // Computed Methods
  getAdmissionsByPatient: (patientId) => {
    const { admissions } = get();
    return admissions.filter(a => a.patientId === patientId);
  },
  
  getActiveAdmissions: () => {
    const { admissions } = get();
    return admissions.filter(a => a.status === 'active');
  },
  
  getAvailableBeds: (wardId) => {
    const { wards } = get();
    const targetWards = wardId ? wards.filter(w => w.id === wardId) : wards;
    const availableBeds: Bed[] = [];
    
    targetWards.forEach(ward => {
      ward.rooms.forEach(room => {
        room.beds.forEach(bed => {
          if (bed.status === 'available' && !bed.isOccupied) {
            availableBeds.push(bed);
          }
        });
      });
    });
    
    return availableBeds;
  },
  
  getBedById: (bedId) => {
    const { wards } = get();
    for (const ward of wards) {
      for (const room of ward.rooms) {
        const bed = room.beds.find(b => b.id === bedId);
        if (bed) return bed;
      }
    }
    return null;
  },
  
  getRoomById: (roomId) => {
    const { wards } = get();
    for (const ward of wards) {
      const room = ward.rooms.find(r => r.id === roomId);
      if (room) return room;
    }
    return null;
  },
  
  getWardById: (wardId) => {
    const { wards } = get();
    return wards.find(w => w.id === wardId) || null;
  },
  
  getADTStats: () => {
    const { admissions, discharges, transfers, wards } = get();
    const activeAdmissions = admissions.filter(a => a.status === 'active');
    
    // Calculate total bed capacity
    let totalBeds = 0;
    let occupiedBeds = 0;
    
    const wardOccupancy = wards.map(ward => {
      let wardBeds = 0;
      let wardOccupied = 0;
      
      ward.rooms.forEach(room => {
        wardBeds += room.beds.length;
        wardOccupied += room.beds.filter(b => b.isOccupied).length;
      });
      
      totalBeds += wardBeds;
      occupiedBeds += wardOccupied;
      
      return {
        wardId: ward.id,
        wardName: ward.name,
        occupancyRate: wardBeds > 0 ? (wardOccupied / wardBeds) * 100 : 0
      };
    });
    
    // Calculate average length of stay
    const completedStays = discharges.filter(d => d.status === 'completed');
    const totalStayDays = completedStays.reduce((sum, discharge) => {
      const admission = admissions.find(a => a.id === discharge.admissionId);
      if (admission) {
        const admitDate = new Date(admission.admissionDate);
        const dischargeDate = new Date(discharge.dischargeDate);
        const days = Math.ceil((dischargeDate.getTime() - admitDate.getTime()) / (1000 * 60 * 60 * 24));
        return sum + days;
      }
      return sum;
    }, 0);
    
    const averageLengthOfStay = completedStays.length > 0 
      ? totalStayDays / completedStays.length 
      : 0;
    
    return {
      totalAdmissions: admissions.length,
      totalDischarges: discharges.length,
      totalTransfers: transfers.length,
      currentOccupancy: activeAdmissions.length,
      averageLengthOfStay,
      bedUtilizationRate: totalBeds > 0 ? (occupiedBeds / totalBeds) * 100 : 0,
      wardOccupancy
    };
  },
  
  getCurrentPatientAdmission: (patientId) => {
    const { admissions } = get();
    return admissions.find(a => a.patientId === patientId && a.status === 'active') || null;
  }
}));